/**
 * AiAssistantPage — In-portal AI Assistant chatbot (EPIC-08-16).
 *
 * Route: /app/assistant
 *
 * Free-form Q&A about insurance products, compliance, what to study next, and how
 * to use the platform. Calls POST /api/v1/portal/assistant/ask, which runs in
 * deterministic mock mode locally and Bedrock Claude in production.
 *
 * This is where the sidebar "Ask AI Assistant → Ask Now" button and the
 * "AI Assistant" nav item land (previously mis-wired to /drill/today).
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import apiClient from '../api/client';
import styles from './AiAssistantPage.module.css';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface AskResponse {
  answer: string;
  citations: { document?: string; section?: string }[];
  confidence: number;
  follow_up_questions: string[];
}

export function AiAssistantPage(): JSX.Element {
  const { t } = useTranslation('training');
  const navigate = useNavigate();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [followUps, setFollowUps] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Suggested starter prompts (shown before the first message)
  const starters = t('assistant.starters', { returnObjects: true }) as unknown as string[];

  useEffect(() => {
    bottomRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  async function ask(question: string): Promise<void> {
    const content = question.trim();
    if (!content || isSending) return;

    setInputValue('');
    setFollowUps([]);
    setMessages((prev) => [...prev, { role: 'user', content }]);
    setIsSending(true);

    try {
      const res = await apiClient.post<AskResponse>('/portal/assistant/ask', {
        question: content,
        language: i18next.language,
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.answer }]);
      setFollowUps(res.data.follow_up_questions ?? []);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: t('assistant.error') },
      ]);
    } finally {
      setIsSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void ask(inputValue);
    }
  }

  const isEmpty = messages.length === 0;

  return (
    <main className={styles.page}>
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={() => navigate('/home')} type="button">
          ← {t('common.button.back')}
        </button>
        <div className={styles.titleBlock}>
          <div className={styles.titleRow}>
            <h1 className={styles.title}>{t('assistant.title', 'AI Assistant')}</h1>
            <span className={styles.headerBadge}>✦ {t('assistant.poweredBy', 'AI-Powered')}</span>
          </div>
          <p className={styles.subtitle}>{t('assistant.subtitle')}</p>
        </div>
      </header>

      <section className={styles.chatArea} aria-label={t('assistant.chatLabel', 'AI Assistant chat')}>
        {isEmpty ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon} aria-hidden="true">
              <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="8" width="18" height="12" rx="3" />
                <path d="M12 8V4" /><circle cx="12" cy="3" r="1" fill="#fff" stroke="none" />
                <circle cx="8.5" cy="14" r="1.3" fill="#fff" stroke="none" />
                <circle cx="15.5" cy="14" r="1.3" fill="#fff" stroke="none" />
                <path d="M9 17.5h6" />
              </svg>
            </div>
            <p className={styles.emptyTitle}>{t('assistant.emptyTitle')}</p>
            <div className={styles.starterGrid}>
              {Array.isArray(starters) &&
                starters.map((s) => (
                  <button
                    key={s}
                    className={styles.starterChip}
                    onClick={() => void ask(s)}
                    type="button"
                  >
                    {s}
                  </button>
                ))}
            </div>
          </div>
        ) : (
          messages.map((msg, i) => (
            <div
              key={i}
              className={msg.role === 'assistant' ? styles.assistantBubble : styles.userBubble}
            >
              <div className={styles.bubbleContent}>{msg.content}</div>
            </div>
          ))
        )}
        {isSending && (
          <div className={styles.assistantBubble}>
            <div className={styles.bubbleContent}>{t('assistant.thinking')}</div>
          </div>
        )}
        <div ref={bottomRef} />
      </section>

      {followUps.length > 0 && !isSending && (
        <div className={styles.followUps} aria-label={t('assistant.followUpsLabel')}>
          {followUps.map((q) => (
            <button
              key={q}
              className={styles.followUpChip}
              onClick={() => void ask(q)}
              type="button"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      <footer className={styles.inputRow}>
        <textarea
          className={styles.input}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t('assistant.inputPlaceholder')}
          rows={2}
          disabled={isSending}
          aria-label={t('assistant.inputLabel', 'Your question')}
        />
        <button
          className={styles.sendBtn}
          onClick={() => void ask(inputValue)}
          disabled={isSending || !inputValue.trim()}
          type="button"
        >
          {isSending ? t('assistant.sending') : t('assistant.sendButton')}
        </button>
      </footer>
    </main>
  );
}
